import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ChevronRight, Mail, Phone, MapPin, MessageCircle, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactPage = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="animate-fade-in">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-stone-200">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl py-4">
          <nav className="flex items-center gap-2 text-sm font-sans" data-testid="breadcrumb">
            <Link to="/" className="text-stone-500 hover:text-deep-moss">
              Home
            </Link>
            <ChevronRight className="w-4 h-4 text-stone-400" />
            <span className="text-stone-900">Contact</span>
          </nav>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-12 md:py-20">
        <div className="grid md:grid-cols-2 gap-12 md:gap-20">
          {/* Contact Info */}
          <div>
            <p className="font-accent text-xs text-stone-500 uppercase tracking-widest mb-2">
              Get In Touch
            </p>
            <h1 className="font-serif text-4xl md:text-5xl font-semibold text-stone-900 mb-6">
              We'd Love to<br />Hear From You
            </h1>
            <p className="font-sans text-stone-600 leading-relaxed mb-10">
              Have questions about our products? Want to know more about our rare plants or herbal remedies? 
              We're here to help! Send us a message and we'll get back to you as soon as possible.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-bone-cream flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-deep-moss" />
                </div>
                <div>
                  <h3 className="font-serif text-lg font-medium text-stone-900">Email</h3>
                  <p className="font-sans text-stone-600">sales@weh.net</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-bone-cream flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-deep-moss" />
                </div>
                <div>
                  <h3 className="font-serif text-lg font-medium text-stone-900">Phone</h3>
                  <p className="font-sans text-stone-600">(504) 358-3906</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-bone-cream flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-deep-moss" />
                </div>
                <div>
                  <h3 className="font-serif text-lg font-medium text-stone-900">Location</h3>
                  <p className="font-sans text-stone-600">Louisiana, USA</p>
                </div>
              </div>
            </div>

            {/* Placeholder for future integrations */}
            <div className="mt-10 pt-8 border-t border-stone-200">
              <p className="font-accent text-xs text-stone-500 uppercase tracking-widest mb-4">
                Coming Soon
              </p>
              <div className="flex flex-wrap gap-3">
                <div className="flex items-center gap-2 bg-stone-100 rounded-full px-4 py-2 text-stone-500 text-sm font-sans">
                  <MessageCircle className="w-4 h-4" />
                  Live Chat
                </div>
                <div className="flex items-center gap-2 bg-stone-100 rounded-full px-4 py-2 text-stone-500 text-sm font-sans">
                  <Send className="w-4 h-4" />
                  WhatsApp
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white p-8 md:p-10 border border-stone-200">
            <h2 className="font-serif text-2xl font-semibold text-stone-900 mb-6">
              Send a Message
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block font-sans text-sm text-stone-700 mb-2">Name</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="contact-name"
                    className="w-full input-underline"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block font-sans text-sm text-stone-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="contact-email"
                    className="w-full input-underline"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div>
                <label className="block font-sans text-sm text-stone-700 mb-2">Subject</label>
                <input
                  type="text"
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  data-testid="contact-subject"
                  className="w-full input-underline"
                  placeholder="How can we help?"
                />
              </div>

              <div>
                <label className="block font-sans text-sm text-stone-700 mb-2">Message</label>
                <textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="contact-message"
                  rows={5}
                  className="w-full input-underline resize-none"
                  placeholder="Tell us more about your question..."
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-btn"
                className="w-full bg-deep-moss hover:bg-deep-moss/90 text-white rounded-full px-8 py-4 font-sans font-medium transition-transform hover:scale-[1.02] disabled:opacity-50"
              >
                {loading ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
